import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, ArrowDownToLine, ClipboardCopy } from 'lucide-react';
import { matchingAPI } from '../../lib/api';
import { useJobPolling, useJobResume, JobProgressLine, JobErrorBlock } from './JobPollingPanel';

function getErrorMessage(err) {
  return err?.response?.data?.detail || err?.message || 'Request failed';
}

// Reason-honesty fix (Eurelo prod incident, tx wise_1990452765): a
// date_outside_window pending row used to render the bare reason string
// with no values, costing the operator manual date lookups to diagnose a
// false refusal. Render whatever corroborate() actually computed --
// window bounds and which member due date produced them when the window
// math ran, or the more specific cause (unparseable/unreliable/missing
// date) when it didn't get that far -- same "render the computed values,
// never a silent label" treatment the Wise Supplier corroboration reasons
// (WiseSupplierReconciliationCard.jsx) already get.
function fmtDateWindowDetail(detail) {
  if (!detail) return '';
  const memberDates = (detail.members || [])
    .map((m) => `${m.invoice_number || m.invoice_id}: ${m.date_used || 'no date'}${m.date_reliable === false ? ' (unreliable)' : ''}`)
    .join(', ');
  if (detail.cause === 'window_exceeded') {
    return ` (window ${detail.window_start}..${detail.window_end}, tx ${detail.tx_date}, due dates: ${memberDates})`;
  }
  if (detail.cause === 'tx_date_unparseable') {
    return ` (transaction date unparseable: ${detail.tx_date ?? '(none)'})`;
  }
  return ` (tx ${detail.tx_date}, due dates: ${memberDates})`;
}

function fmtPendingReason(p) {
  if (p.reason === 'date_outside_window' && p.reason_detail) {
    return p.reason + fmtDateWindowDetail(p.reason_detail);
  }
  return p.reason;
}

// Single place to read "the number this example actually matched" -- the
// live resolver's own canonical pool (services/inflow_reference_matching.py
// ::build_matching_candidate_pool), so field_examples has one uniform shape
// across both the "twinned_bros" and "untwinned_xero" sides.
function matchedNumberFor(example) {
  return example.matched_invoice_number ?? '-';
}

const TALLY_LABELS = {
  seen: 'Seen',
  reconciled: 'Reconciled',
  already_applied_in_xero: 'Already applied in Xero',
  no_tokens: 'No reference tokens',
  unresolved_token: 'Unresolved token',
  counterparty_mismatch: 'Counterparty mismatch',
  currency_mismatch: 'Currency mismatch',
  amount_mismatch: 'Amount mismatch',
  date_out_of_window: 'Date out of window',
  confirm_failed: 'Confirm failed',
};

export function InflowReferenceMatchingPanel() {
  const [preview, setPreview] = useState(null);
  const [previewStarting, setPreviewStarting] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(null);
  const [previewProgress, setPreviewProgress] = useState(null);

  const [applyStarting, setApplyStarting] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [applyFailed, setApplyFailed] = useState(null);
  const [applyProgress, setApplyProgress] = useState(null);

  const [recentRows, setRecentRows] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [fieldSample, setFieldSample] = useState(null);
  const [fieldSampleLoading, setFieldSampleLoading] = useState(false);
  const [fieldSampleError, setFieldSampleError] = useState(null);
  const [fieldSampleCopied, setFieldSampleCopied] = useState(false);

  const refreshRecent = async () => {
    setRecentLoading(true);
    try {
      const res = await matchingAPI.recentInflowReferenceMatches();
      setRecentRows(res.data.rows || []);
    } catch (err) {
      // Non-fatal -- the Preview/Apply flow above is the primary surface.
    } finally {
      setRecentLoading(false);
    }
  };

  useEffect(() => { refreshRecent(); }, []);

  const previewPoll = useJobPolling({
    fetchStatus: matchingAPI.inflowReferenceMatchingJobStatus,
    onCompleted: useCallback((result) => {
      setPreview(result);
      setPreviewFailed(null);
      setPreviewProgress(null);
    }, []),
    onFailed: useCallback((error) => {
      setPreviewFailed(error);
      setPreviewProgress(null);
    }, []),
    onProgress: useCallback((progress) => {
      setPreviewProgress(progress);
    }, []),
  });

  const applyPoll = useJobPolling({
    fetchStatus: matchingAPI.inflowReferenceMatchingJobStatus,
    onCompleted: useCallback((result) => {
      setApplyResult(result);
      setApplyFailed(null);
      setApplyProgress(null);
      refreshRecent();
    }, []),
    onFailed: useCallback((error) => {
      setApplyFailed(error);
      setApplyProgress(null);
    }, []),
    onProgress: useCallback((progress) => {
      setApplyProgress(progress);
    }, []),
  });

  useJobResume({ fetchLatest: useCallback(() => matchingAPI.inflowReferenceMatchingJobLatest(true), []), poll: previewPoll });
  useJobResume({ fetchLatest: useCallback(() => matchingAPI.inflowReferenceMatchingJobLatest(false), []), poll: applyPoll });

  const runPreview = async () => {
    setPreviewStarting(true);
    setPreviewFailed(null);
    setPreviewProgress(null);
    setApplyResult(null);
    setApplyFailed(null);
    try {
      const { data } = await matchingAPI.inflowReferenceMatchingStart(true);
      if (data.job_id) {
        previewPoll.start(data.job_id);
      }
    } catch (err) {
      setPreviewFailed(getErrorMessage(err) || 'Failed to start preview.');
    } finally {
      setPreviewStarting(false);
    }
  };

  const previewBusy = previewStarting || previewPoll.polling;

  const runApply = async () => {
    if (!window.confirm(
      `Reconcile ${preview?.tallies?.reconciled || 0} inflow(s) whose reference resolves to a ` +
      'BR OS invoice now? Any transaction with an unresolved/ambiguous token, or a corroboration ' +
      'mismatch, stays pending with a reason stamped, never silently skipped.'
    )) return;
    setApplyStarting(true);
    setApplyFailed(null);
    setApplyResult(null);
    setApplyProgress(null);
    try {
      const { data } = await matchingAPI.inflowReferenceMatchingStart(false);
      if (data.job_id) {
        applyPoll.start(data.job_id);
      }
    } catch (err) {
      setApplyFailed(getErrorMessage(err) || 'Failed to start apply.');
    } finally {
      setApplyStarting(false);
    }
  };

  const applyBusy = applyStarting || applyPoll.polling;

  const runFieldSample = async () => {
    setFieldSampleLoading(true);
    setFieldSampleError(null);
    try {
      const res = await matchingAPI.fieldSampleInflowReferenceMatching();
      setFieldSample(res.data);
    } catch (err) {
      setFieldSampleError(getErrorMessage(err));
    } finally {
      setFieldSampleLoading(false);
    }
  };

  const copyFieldSample = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(fieldSample, null, 2));
      setFieldSampleCopied(true);
      setTimeout(() => setFieldSampleCopied(false), 2000);
    } catch (err) {
      setFieldSampleError('Could not copy to clipboard: ' + getErrorMessage(err));
    }
  };

  const hitCounts = fieldSample?.field_hit_counts || {};
  const maxHits = Math.max(0, ...Object.values(hitCounts));
  const winningField = maxHits > 0
    ? Object.keys(hitCounts).find((f) => hitCounts[f] === maxHits)
    : null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <ArrowDownToLine className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Inflow Reference Matches</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Matches an unreconciled client inflow against a BR OS invoice number parsed straight out
          of the bank reference (e.g. "Ref: Invoice INV-212402", or a comma list like "/283, 284").
          Every parsed number must resolve to exactly one open BR OS invoice; counterparty, amount
          (fee-tolerant, summed across multiple invoices) and date must all corroborate. Zero or
          ambiguous resolution, or any mismatch, leaves the transaction pending with the reason
          stamped -- never guessed. Also runs automatically on the existing auto-matching schedule.
          Preview/Apply run in the background and this panel polls for progress -- it is safe to
          navigate away and come back.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Button
            variant="outline" size="sm" onClick={runPreview} disabled={previewBusy}
            data-testid="inflow-reference-matching-preview"
          >
            {previewBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Preview
          </Button>
          {preview && !preview.error && preview.tallies?.reconciled > 0 && (
            <Button
              variant="default" size="sm" onClick={runApply}
              disabled={applyBusy}
              data-testid="inflow-reference-matching-apply"
            >
              {applyBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Apply ({preview.tallies.reconciled})
            </Button>
          )}
          <Button
            variant="outline" size="sm" onClick={runFieldSample} disabled={fieldSampleLoading}
            data-testid="inflow-reference-field-sample-button"
          >
            {fieldSampleLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Field sample
          </Button>
        </div>

        {fieldSampleError && (
          <p className="text-xs text-red-600" data-testid="inflow-reference-field-sample-error">
            {fieldSampleError}
          </p>
        )}

        {fieldSample && !fieldSampleError && (
          <div className="space-y-2 border rounded-lg p-3" data-testid="inflow-reference-field-sample-result">
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                {fieldSample.unresolved_token_count ?? 0} unresolved token(s) checked ·
                {' '}Xero connected: {fieldSample.xero_connected ? 'yes' : 'no'}
              </p>
              <Button
                variant="outline" size="sm" onClick={copyFieldSample}
                data-testid="inflow-reference-field-sample-copy"
              >
                <ClipboardCopy className="h-3 w-3 mr-1" />
                {fieldSampleCopied ? 'Copied' : 'Copy result'}
              </Button>
            </div>

            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-xs" data-testid="inflow-reference-field-sample-table">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-2 py-1 font-medium">Field</th>
                    <th className="text-left px-2 py-1 font-medium">Hits</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(hitCounts).map(([field, hits]) => (
                    <tr
                      key={field}
                      className={`border-b last:border-0 ${field === winningField ? 'bg-green-50 dark:bg-green-950 font-semibold' : ''}`}
                      data-testid={field === winningField ? 'inflow-reference-field-sample-winner' : undefined}
                    >
                      <td className="px-2 py-1 font-mono">
                        {field}{field === winningField ? ' ← winning field' : ''}
                      </td>
                      <td className="px-2 py-1">{hits}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {Object.entries(fieldSample.field_examples || {}).map(([field, examples]) => (
              (examples || []).length > 0 && (
                <div key={field} className="space-y-1">
                  <p className="text-xs font-medium">{field}</p>
                  <ul className="text-xs space-y-0.5" data-testid={'inflow-reference-field-sample-examples-' + field}>
                    {examples.map((ex, i) => (
                      <li key={i} className="font-mono">
                        {ex.token} &rarr; {matchedNumberFor(ex)}{ex.client ? ` (${ex.client})` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )
            ))}

            {(fieldSample.unmatched_sample || []).length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
                  Unmatched by any candidate field
                </p>
                <ul className="text-xs space-y-0.5">
                  {fieldSample.unmatched_sample.map((u, i) => (
                    <li key={i} className="font-mono text-amber-700 dark:text-amber-400">
                      {u.token} ({u.reference || '(no reference)'})
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {previewPoll.polling && (
          <JobProgressLine progress={previewProgress} testId="inflow-reference-matching-preview-progress">
            {(p) => <>Scanned {p.scanned ?? 0} of {p.total ?? '?'} ({p.seen ?? 0} in population)</>}
          </JobProgressLine>
        )}

        <JobErrorBlock error={previewFailed} testId="inflow-reference-matching-preview-error" />

        {preview && (
          <div className="space-y-2" data-testid="inflow-reference-matching-preview-result">
            <div className="flex flex-wrap gap-2">
              {preview.tallies && Object.entries(preview.tallies).map(([key, count]) => (
                <Badge key={key} variant="outline" className="text-[10px]" data-testid={'inflow-reference-tally-' + key}>
                  {TALLY_LABELS[key] || key}: {count}
                </Badge>
              ))}
            </div>
            {preview.reconciled && preview.reconciled.length > 0 && (
              <ul className="text-xs space-y-1" data-testid="inflow-reference-reconciled-list">
                {preview.reconciled.map((r) => (
                  <li key={r.transaction_id} className="font-mono">
                    tx {r.transaction_id} · {r.client} · {(r.invoice_numbers || []).join(' + ')} ·
                    {' '}{r.amount} {r.currency}
                    {r.already_applied_in_xero && (
                      <span
                        className="ml-1 text-amber-700 dark:text-amber-400"
                        data-testid={'inflow-reference-already-applied-' + r.transaction_id}
                      >
                        (already applied in Xero)
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {preview.pending && preview.pending.length > 0 && (
              <ul className="text-xs space-y-1" data-testid="inflow-reference-pending-list">
                {preview.pending.map((p) => (
                  <li key={p.transaction_id} className="font-mono text-amber-700 dark:text-amber-400">
                    tx {p.transaction_id} · {p.reference || '(no reference)'} · {fmtPendingReason(p)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {applyPoll.polling && (
          <JobProgressLine progress={applyProgress} testId="inflow-reference-matching-apply-progress">
            {(p) => <>Scanned {p.scanned ?? 0} of {p.total ?? '?'} ({p.seen ?? 0} in population)</>}
          </JobProgressLine>
        )}

        <JobErrorBlock error={applyFailed} testId="inflow-reference-matching-apply-error" />

        {applyResult && !applyFailed && (
          <div
            className="p-3 rounded text-xs border bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-900"
            data-testid="inflow-reference-matching-apply-result"
          >
            <p><strong>Reconciled:</strong> {applyResult.tallies?.reconciled ?? 0}</p>
            <p><strong>Already applied in Xero:</strong> {applyResult.tallies?.already_applied_in_xero ?? 0}</p>
          </div>
        )}

        <div className="space-y-1">
          <p className="text-xs font-medium">Recent inflow reference matches (last 50)</p>
          {recentLoading && recentRows.length === 0 ? (
            <div className="text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin inline mr-1" />Loading...</div>
          ) : recentRows.length === 0 ? (
            <div className="text-xs text-muted-foreground" data-testid="inflow-reference-recent-empty">
              No inflow reference matches yet.
            </div>
          ) : (
            <div className="overflow-x-auto border rounded-lg max-h-56 overflow-y-auto" data-testid="inflow-reference-recent">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50 sticky top-0">
                    <th className="text-left px-2 py-1 font-medium">Invoice(s)</th>
                    <th className="text-left px-2 py-1 font-medium">Client</th>
                    <th className="text-left px-2 py-1 font-medium">Transaction</th>
                    <th className="text-left px-2 py-1 font-medium">Bank fee</th>
                    <th className="text-left px-2 py-1 font-medium">Xero</th>
                    <th className="text-left px-2 py-1 font-medium">When</th>
                  </tr>
                </thead>
                <tbody>
                  {recentRows.map((r) => (
                    <tr key={r.id} className="border-b last:border-0">
                      <td className="px-2 py-1 font-mono">{(r.invoice_numbers || []).join(' + ')}</td>
                      <td className="px-2 py-1">{r.client || '-'}</td>
                      <td className="px-2 py-1 font-mono">{r.transaction_id}</td>
                      <td className="px-2 py-1">{r.evidence?.bank_fee_amount ?? '-'}</td>
                      <td className="px-2 py-1">
                        {r.already_applied_in_xero ? (
                          <span className="text-amber-700 dark:text-amber-400">already applied</span>
                        ) : '-'}
                      </td>
                      <td className="px-2 py-1 whitespace-nowrap">{r.matched_at ? new Date(r.matched_at).toLocaleString('en-GB') : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
